<template>
  <info-panel
    v-if="model"
    :item="model"
    :title="$t('headings.modelInformation') + ' – ' + modelTitle"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>{{ $t("labels.showEmptySections") }}</template>

    <!-- simulation game data -->
    <template #label-gameInfo>
      {{ $t('headings.gameInformation') }}
    </template>
    <template v-if="gameAuthor" #gameInfo>
      <p>
        {{ $t("labels.name") }}{{ $t(":") }} {{ gameName }}<br />
        {{ $t("labels.author") }}{{ $t(":") }} {{ gameAuthor }}
      </p>
    </template>

    <template #label-description>
      {{ $t('labels.description') }}
    </template>
    <template #description v-if="settingsValues.showEmptySections || !isEmpty(modelDescription)">
      <formatted-read-more-text
        :value="modelDescription"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <template #label-question>
      {{ $t("labels.question") }}
    </template>
    <template #question v-if="settingsValues.showEmptySections || !isEmpty(question.text)">
      <formatted-text
        :value="question.text"
        :placeholder="$t('errors.noQuestion')"
      />
    </template>

    <!-- stakeholders -->
    <template #label-stakeholders>
      {{ $t("objectNames.stakeholder", 2) }}
    </template>
    <template #stakeholders v-if="settingsValues.showEmptySections || stakeholders.length > 0">
      <div class="line--wrap">
        <button-item
          v-for="stakeholder of stakeholders"
          :key="'stakeholder' + stakeholder.id"
          @click="openOverlay(stakeholder)"
        >
          <span v-if="stakeholder.title">
            {{ stakeholder.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
      </div>
      <div v-if="stakeholders.length == 0">
        {{ $t("errors.noX", [$t("objectNames.stakeholder", 2)]) }}
      </div>
    </template>

    <!-- targets -->
    <template #label-targets>
      {{ $t("objectNames.target", 2) }}
    </template>
    <template #targets v-if="settingsValues.showEmptySections || targets.length > 0">
      <div class="line--wrap">
        <button-item
          v-for="target of targets"
          :key="'target' + target.id"
          @click="openOverlay(target)"
          @pointerover="setHighlight(target)"
          @pointerout="removeHighlight(target)"
        >
          <span v-if="target.title">
            {{ target.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
        <div v-if="targets.length == 0">
          {{ $t("errors.noX", [$t("objectNames.target", 2)]) }}
        </div>
      </div>
    </template>

    <!-- resources -->
    <template #label-resources>
      {{ $t("objectNames.resource", 2) }}
    </template>
    <template #resources v-if="settingsValues.showEmptySections || resources.length > 0">
      <div class="line--wrap">
        <button-item
          v-for="resource of resources"
          :key="'resource' + resource.id"
          @click="openOverlay(resource)"
        >
          <span v-if="resource.title">
            {{ resource.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
        <div v-if="resources.length == 0">
          {{ $t("errors.noX", [$t("objectNames.resource", 2)]) }}
        </div>
      </div>
    </template>

    <!-- INDICES -->
    <template #label-indices>
      {{ $t("objectNames.index", 2) }}
    </template>
    <template #indices v-if="settingsValues.showEmptySections || indices.length > 0">
      <div class="line--wrap">
        <button-item
          v-for="index of indices"
          :key="'index' + index.id"
          @click="openOverlay(index)"
        >
          <span v-if="index.title">
            {{ index.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
        <div v-if="indices.length == 0">
          {{ $t("errors.noX", [$t("objectNames.index", 2)]) }}
        </div>
      </div>
    </template>

    <!-- ACTIONS -->
    <template #label-actions>
      {{ $t("objectNames.action", 2) }}
    </template>
    <template #actions v-if="settingsValues.showEmptySections || actions.length > 0">
      <div class="line--wrap">
        <button-item
          v-for="action of actions"
          :key="'action' + action.id"
          @click="openOverlay(action)"
        >
          <span v-if="action.title">
            {{ action.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
        <div v-if="actions.length == 0">
          {{ $t("errors.noX", [$t("objectNames.action", 2)]) }}
        </div>
      </div>
    </template>

    <!-- INCIDENTS -->
    <template #label-events>
      {{ $t("objectNames.incident", 2) }}
    </template>
    <template #events v-if="settingsValues.showEmptySections || events.length > 0">
      <div class="line--wrap">
        <button-item
          v-for="incident of events"
          :key="'incident' + incident.id"
          @click="openOverlay(incident)"
        >
          <span v-if="incident.title">
            {{ incident.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
        <div v-if="events.length == 0">
          {{ $t("errors.noX", [$t("objectNames.incident", 2)]) }}
        </div>
      </div>
    </template>

    <!-- SCENARIOS -->
    <template #label-scenarios>
      {{ $t("objectNames.scenario", 2) }}
    </template>
    <template #scenarios v-if="settingsValues.showEmptySections || scenarios.length > 0">
      <div class="line--wrap">
        <button-item
          v-for="scenario of scenarios"
          :key="'scenario' + scenario.id"
          @click="openOverlay(scenario)"
        >
          <span v-if="scenario.title">
            {{ scenario.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
        <div v-if="scenarios.length == 0">
          {{ $t("errors.noX", [$t("objectNames.scenario", 2)]) }}
        </div>
      </div>
    </template>

    <template #label-simulations>
      {{ $t("objectNames.simulation", 2) }}
    </template>
    <template #simulations v-if="settingsValues.showEmptySections || simulations.length > 0">
      <div class="line--wrap">
        <button-item
          v-for="simulation of simulations"
          :key="'simulation' + simulation.id"
          @click="openOverlay(simulation)"
        >
          <span v-if="simulation.title">
            {{ simulation.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
        <div v-if="simulations.length == 0">
          {{ $t("errors.noX", [$t("objectNames.simulation", 2)]) }}
        </div>
      </div>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { isEmpty } from "@/helpers/utility/Text";
import { mapValues } from "@/helpers/utility/CollectionFunctions";

import InfoPanel from "@editor/ui/InfoPanel.vue";

import FormattedText from "@core/ui/FormattedText.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import ButtonItem from "@core/input/ButtonItem.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import { useActionStore } from "../action/actions.store";
import { useIncidentStore } from "../incident/incidents.store";
import { useScenarioStore } from "../scenario/scenarios.store";
import { useSimulationStore } from "../simulation/simulations.store";
import { useProjectStore } from "@editor/store/project.store";
import { useQuestionStore } from "../question/question.store";
import { useStakeholderStore } from "../stakeholder/stakeholders.store";
import { useLoadedModel } from "@editor/store/loadedModel.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";

import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";

defineProps<{
  model: { type: "model" } | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const infoPanelStore = useInfoPanelStore();

const sections = reactive({
  gameInfo: true,
  description: true,
  question: true,
  targets: true,
  resources: true,
  indices: true,
  actions: true,
  events: true,
  stakeholders: true,
  scenarios: true,
  simulations: true
});

const extraSettings = reactive({
  showEmptySections: { type: "check" as const, value: true }
});

// const features = useFeatures();
const { setHighlight, removeHighlight } = useSelectionStore();
const loadedModel = computed(() => useLoadedModel().loadedModel);

const modelTitle = computed(() => {
  return useProjectStore().title || loadedModel.value.name;
});
const modelDescription = computed(() => useProjectStore().description);
const stakeholders = computed(() => useStakeholderStore().orderedItems);
const question = computed(() => useQuestionStore());
const targets = computed(() => useTargetStore().orderedItems);
const resources = computed(() => useResourceStore().orderedItems);
const indices = computed(() => useIndexStore().orderedItems);
const actions = computed(() => useActionStore().orderedItems);
const events = computed(() => useIncidentStore().orderedItems);
const scenarios = computed(() => useScenarioStore().orderedItems);
const simulations = computed(() => useSimulationStore().orderedItems);
const gameName = computed(() => useProjectStore().exportedModelName);
const gameAuthor = computed(() => useProjectStore().author);


function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}



function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.model = sections;
}

function loadSections() {
  const savedSections = infoPanelStore.sections?.model || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as (keyof typeof sections)] = savedSections[key];
    }
  }
}

loadSections();



function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.model = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.model || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as (keyof typeof extraSettings)] = { ...(savedExtraSettings[key] as any) };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
</script>

<style lang="scss"></style>